import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import { Search, Calendar, Eye, FileText, Printer, Download, ChevronLeft, ChevronRight, X } from 'lucide-react';
import './ReceiptHistory.css';

const ReceiptHistory = () => {
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [monthFilter, setMonthFilter] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;

    useEffect(() => {
        fetchReceipts();
    }, []);

    const fetchReceipts = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/receipts`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (response.ok) {
                const data = await response.json();
                setReceipts(data);
            }
        } catch (error) {
            console.error('Error fetching receipts:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleInvoiceStatus = async (receiptId, currentStatus) => {
        try {
            const newStatus = !currentStatus;
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/receipts/${receiptId}/invoice-status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ factura_entregada: newStatus ? 1 : 0 })
            });

            if (res.ok) {
                // Update local state
                setReceipts(prevReceipts => prevReceipts.map(r =>
                    r.id === receiptId ? { ...r, factura_entregada: newStatus ? 1 : 0 } : r
                ));
                if (selectedReceipt && selectedReceipt.id === receiptId) {
                    setSelectedReceipt({ ...selectedReceipt, factura_entregada: newStatus ? 1 : 0 });
                }
            } else {
                alert('Error al actualizar el estado de la factura');
            }
        } catch (error) {
            console.error('Error toggling invoice status:', error);
            alert('Error de red al actualizar la factura');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        // Handle both YYYY-MM-DD and YYYY-MM-DD HH:MM:SS
        const cleanDate = dateStr.toString().split('T')[0].split(' ')[0];
        const parts = cleanDate.split('-');

        // Ensure we have year, month, day
        if (parts.length === 3) {
            const [year, month, day] = parts;
            return `${day}/${month}/${year}`;
        }

        // Fallback for unexpected formats
        return dateStr;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount || 0);
    };

    const getItemSubtotal = (item) => {
        if (!item) return 0;

        const explicitSubtotal = parseFloat(item.subtotal);
        if (!Number.isNaN(explicitSubtotal)) {
            return explicitSubtotal;
        }

        const total = parseFloat(item.total);
        if (Number.isNaN(total)) {
            return 0;
        }

        const ivaPctRaw = parseFloat(item.iva_porcentaje ?? item.iva ?? 16);
        const ivaPct = Number.isNaN(ivaPctRaw) ? 16 : Math.max(0, ivaPctRaw);
        const isExempt = !!(item.is_exempt || item.isExempt || ivaPct === 0);
        if (isExempt) {
            return total;
        }

        const divisor = 1 + (ivaPct / 100);
        if (divisor <= 0) {
            return total;
        }
        return total / divisor;
    };

    const formatPaymentMethod = (method) => {
        if (!method) return '-';
        const m = method.toLowerCase();
        if (m.includes('debito') || m.includes('debi')) return 'Tarjeta de Débito';
        if (m.includes('credito') || m.includes('cred')) return 'Tarjeta de Crédito';
        if (m.includes('tarjeta')) return 'Tarjeta';
        if (m.includes('transferencia')) return 'Transferencia';
        if (m === 'efectivo') return 'Efectivo';
        if (m === 'cheque') return 'Cheque';
        if (m === 'mixto') return 'Mixto';
        return method.charAt(0).toUpperCase() + method.slice(1).replace(/_/g, ' ');
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'pagado': { label: 'Pagado', class: 'status-paid' },
            'pendiente': { label: 'Pendiente', class: 'status-pending' },
            'cancelado': { label: 'Cancelado', class: 'status-cancelled' }
        };
        const statusInfo = statusMap[status] || { label: status, class: 'status-default' };
        return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
    };

    // Filter receipts by search term and month
    const filteredReceipts = receipts.filter(receipt => {
        const search = searchTerm.toLowerCase();
        const matchesSearch = (
            receipt.folio?.toLowerCase().includes(search) ||
            receipt.patient_name?.toLowerCase().includes(search) ||
            (receipt.first_name && `${receipt.first_name} ${receipt.last_name} ${receipt.maternal_last_name || ''}`.toLowerCase().includes(search)) ||
            receipt.concepto?.toLowerCase().includes(search)
        );

        // Month filter logic with safety checks
        let matchesMonth = true;
        if (monthFilter && receipt.fecha) {
            const dateStr = String(receipt.fecha);
            if (dateStr.length >= 7) {
                const receiptMonth = dateStr.substring(0, 7); // YYYY-MM
                matchesMonth = receiptMonth === monthFilter;
            } else {
                matchesMonth = false;
            }
        } else if (monthFilter && !receipt.fecha) {
            matchesMonth = false;
        }

        return matchesSearch && matchesMonth;
    });

    // Pagination
    const totalPages = Math.ceil(filteredReceipts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedReceipts = filteredReceipts.slice(startIndex, startIndex + itemsPerPage);

    const handlePrint = (receipt) => {
        const printWindow = window.open('', '_blank', 'width=800,height=900');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Recibo ${receipt.folio}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; }
                    .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 15px; }
                    .header h1 { font-size: 18px; color: #083677; margin-bottom: 5px; }
                    .header p { font-size: 12px; color: #666; }
                    .folio { font-size: 14px; font-weight: bold; margin: 10px 0; }
                    .section { margin: 15px 0; }
                    .section-title { font-size: 11px; text-transform: uppercase; color: #666; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-bottom: 10px; }
                    .row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 13px; }
                    .row .label { color: #555; }
                    .row .value { font-weight: 500; }
                    .total-row { border-top: 2px solid #333; margin-top: 10px; padding-top: 10px; font-size: 16px; font-weight: bold; }
                    .total-row .value { color: #00838F; }
                    .footer { text-align: center; margin-top: 30px; padding-top: 15px; border-top: 1px dashed #999; font-size: 11px; color: #666; }
                    @media print { body { padding: 10px; } }
                </style>
            </head>
            <body>
                <div class="header">
                    <img src="${window.location.origin}/logo.png" alt="GeneCenter" style="max-width: 200px; max-height: 80px; margin: 0 auto 10px; display: block;" />
                    <p>Recibo de Pago</p>
                </div>
                <div class="folio">Folio: ${receipt.folio}</div>
                <div class="row"><span class="label">Fecha:</span><span class="value">${formatDate(receipt.fecha)}</span></div>
                
                <div class="section">
                    <div class="section-title">Datos del Paciente</div>
                    <div class="row"><span class="label">Nombre:</span><span class="value">${(receipt.first_name ? `${receipt.first_name} ${receipt.last_name} ${receipt.maternal_last_name || ''}` : receipt.patient_name) || '-'}</span></div>
                </div>
                
                <div class="section">
                    <div class="section-title">Detalle del Servicio</div>
                    ${(() => {
                const maxServices = 3;
                let items = [];

                if (receipt.items && receipt.items.length > 0) {
                    items = receipt.items.slice(0, maxServices).map(item =>
                        `<div class="row" style="margin-bottom: 5px; min-height: 22px;"><span class="label">${item.descripcion || item.name}</span><span class="value">${formatCurrency(getItemSubtotal(item))}</span></div>`
                    );
                } else if (receipt.concepto) {
                    items = [`<div class="row" style="margin-bottom: 5px; min-height: 22px;"><span class="label">${receipt.concepto}</span><span class="value">${formatCurrency(receipt.subtotal)}</span></div>`];
                }

                // Fill remaining slots with empty placeholders
                const emptySlots = maxServices - items.length;
                for (let i = 0; i < emptySlots; i++) {
                    items.push(`<div class="row" style="margin-bottom: 5px; min-height: 22px;"><span class="label">&nbsp;</span><span class="value">&nbsp;</span></div>`);
                }

                return items.join('');
            })()}
                </div>
                
                <div class="section">
                    <div class="section-title">Resumen de Pago</div>
                    <div class="row"><span class="label">Subtotal:</span><span class="value">${formatCurrency(receipt.subtotal)}</span></div>
                    <div class="row"><span class="label">Descuento:</span><span class="value" style="color: #e53935;">${parseFloat(receipt.descuento) > 0 ? `-${formatCurrency(receipt.descuento)}` : formatCurrency(0)}</span></div>
                    ${receipt.iva > 0 ? `<div class="row"><span class="label">IVA:</span><span class="value">${formatCurrency(receipt.iva)}</span></div>` : ''}
                    <div class="row total-row"><span class="label">TOTAL:</span><span class="value">${formatCurrency(receipt.total)}</span></div>
                </div>
                
                <div class="row"><span class="label">Forma de Pago:</span><span class="value">${formatPaymentMethod(receipt.metodo_pago || receipt.forma_pago)}</span></div>
                <div class="row"><span class="label">Estado:</span><span class="value">${receipt.estado === 'pagado' ? 'PAGADO' : receipt.estado?.toUpperCase()}</span></div>
                
                
                
                <div class="footer">
                    <p>Gracias por su preferencia</p>
                    <p>Este recibo es un comprobante de pago</p>
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    return (
        <div className="receipt-history-container">
            <div className="receipt-history-header">
                <h2><FileText size={24} /> Historial de Recibos</h2>
                <div className="header-actions">
                    <div className="month-filter-wrapper">
                        {monthFilter && (
                            <button 
                                className="btn-clear-filter-outside" 
                                onClick={() => {
                                    setMonthFilter('');
                                    setCurrentPage(1);
                                }}
                                title="Limpiar filtro de mes"
                            >
                                <X size={16} />
                            </button>
                        )}
                        <div className="filter-group">
                            <input
                                type="month"
                                value={monthFilter}
                                onChange={(e) => {
                                    setMonthFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="month-picker-input"
                            />
                        </div>
                    </div>
                    <div className="search-box">
                        <input
                            type="text"
                            placeholder="Buscar por folio, paciente..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-state">Cargando recibos...</div>
            ) : filteredReceipts.length === 0 ? (
                <div className="empty-state">
                    <FileText size={48} />
                    <p>No hay recibos registrados</p>
                </div>
            ) : (
                <>
                    <div className="receipts-table-wrapper">
                        <table className="receipts-table">
                            <thead>
                                <tr>
                                    <th>Folio</th>
                                    <th>Fecha</th>
                                    <th>Paciente</th>
                                    <th>Concepto</th>
                                    <th>Total</th>
                                    <th>Estado</th>
                                    <th>Notas</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedReceipts.map((receipt) => (
                                    <tr key={receipt.id}>
                                        <td className="folio-cell">{receipt.folio}</td>
                                        <td>{formatDate(receipt.fecha)}</td>
                                        <td>{(receipt.first_name ? `${receipt.first_name} ${receipt.last_name} ${receipt.maternal_last_name || ''}` : receipt.patient_name) || '-'}</td>
                                        <td className="concepto-cell">
                                            {receipt.items && receipt.items.length > 0 ? (
                                                receipt.items.map(item => item.descripcion || item.name).join(', ')
                                            ) : (
                                                receipt.concepto || '-'
                                            )}
                                        </td>
                                        <td className="amount-cell">{formatCurrency(receipt.total)}</td>
                                        <td>
                                            {getStatusBadge(receipt.estado)}
                                            {receipt.solicita_factura == 1 && (
                                                <div style={{ marginTop: '0.25rem' }}>
                                                    <span className={`status-badge`} style={{ backgroundColor: receipt.factura_entregada == 1 ? '#dcfce7' : '#fef9c3', color: receipt.factura_entregada == 1 ? '#166534' : '#854d0e', borderColor: receipt.factura_entregada == 1 ? '#bbf7d0' : '#fef08a', fontSize: '10px', padding: '2px 6px' }}>
                                                        {receipt.factura_entregada == 1 ? 'Facturada' : 'Factura Pendiente'}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="notes-cell">
                                            <div className="notes-column-cell" title={receipt.notas}>
                                                {receipt.notas || '-'}
                                            </div>
                                        </td>
                                        <td className="actions-cell">
                                            <div className="actions-wrapper">
                                                <button
                                                    className="action-btn view-btn"
                                                    onClick={() => setSelectedReceipt(receipt)}
                                                    title="Ver detalle"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button
                                                    className="action-btn print-btn"
                                                    onClick={() => handlePrint(receipt)}
                                                    title="Imprimir"
                                                >
                                                    <Printer size={16} />
                                                </button>
                                            </div>
                                        </td>

                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {
                        totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <span>Página {currentPage} de {totalPages}</span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        )
                    }
                </>
            )}

            {/* Receipt Detail Modal */}
            {
                selectedReceipt && (
                    <div className="modal-overlay" onClick={() => setSelectedReceipt(null)}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Recibo {selectedReceipt.folio}</h3>
                                <button className="close-btn" onClick={() => setSelectedReceipt(null)}>-</button>
                            </div>
                            <div className="modal-body receipt-style">
                                {/* Receipt Header Info */}
                                <div className="receipt-info-row">
                                    <div className="receipt-info-item">
                                        <span className="info-label">Folio</span>
                                        <span className="info-value folio">{selectedReceipt.folio}</span>
                                    </div>
                                    <div className="receipt-info-item">
                                        <span className="info-label">Fecha</span>
                                        <span className="info-value">{formatDate(selectedReceipt.fecha)}</span>
                                    </div>
                                </div>

                                {/* Patient Section */}
                                <div className="receipt-section">
                                    <div className="receipt-section-title">Datos del Paciente</div>
                                    <div className="receipt-row">
                                        <span className="row-label">Nombre:</span>
                                        <span className="row-value">{(selectedReceipt.first_name ? `${selectedReceipt.first_name} ${selectedReceipt.last_name} ${selectedReceipt.maternal_last_name || ''}` : selectedReceipt.patient_name) || '-'}</span>
                                    </div>
                                </div>

                                {/* Service Detail Section */}
                                <div className="receipt-section">
                                    <div className="receipt-section-title">Detalle del Servicio</div>
                                    {(() => {
                                        const maxServices = 3;
                                        let items = [];

                                        if (selectedReceipt.items && selectedReceipt.items.length > 0) {
                                            items = selectedReceipt.items.slice(0, maxServices);
                                        } else if (selectedReceipt.concepto) {
                                            items = [{ descripcion: selectedReceipt.concepto, subtotal: selectedReceipt.subtotal }];
                                        }

                                        const emptySlots = maxServices - items.length;
                                        const allRows = [...items];
                                        for (let i = 0; i < emptySlots; i++) {
                                            allRows.push({ empty: true });
                                        }

                                        return allRows.map((item, idx) => (
                                            <div key={idx} className={`receipt-row service-row ${item.empty ? 'empty-row' : ''}`}>
                                                <span className="row-label">{item.empty ? '\u00A0' : (item.descripcion || item.name)}</span>
                                                <span className="row-value">{item.empty ? '\u00A0' : formatCurrency(getItemSubtotal(item))}</span>
                                            </div>
                                        ));
                                    })()}
                                </div>

                                {/* Payment Summary Section */}
                                <div className="receipt-section">
                                    <div className="receipt-section-title">Resumen de Pago</div>
                                    <div className="receipt-row">
                                        <span className="row-label">Subtotal:</span>
                                        <span className="row-value">{formatCurrency(selectedReceipt.subtotal)}</span>
                                    </div>
                                    <div className="receipt-row">
                                        <span className="row-label">Descuento:</span>
                                        <span className="row-value discount">{parseFloat(selectedReceipt.descuento) > 0 ? `-${formatCurrency(selectedReceipt.descuento)}` : formatCurrency(0)}</span>
                                    </div>
                                    <div className="receipt-row">
                                        <span className="row-label">IVA:</span>
                                        <span className="row-value">{formatCurrency(selectedReceipt.iva || 0)}</span>
                                    </div>
                                    <div className="receipt-row total-row">
                                        <span className="row-label">TOTAL:</span>
                                        <span className="row-value total">{formatCurrency(selectedReceipt.total)}</span>
                                    </div>
                                </div>

                                {/* Additional Info Section */}
                                <div className="receipt-section">
                                    <div className="receipt-section-title">Información Adicional</div>
                                    <div className="receipt-row">
                                        <span className="row-label">Forma de Pago:</span>
                                        <span className="row-value">{formatPaymentMethod(selectedReceipt.metodo_pago || selectedReceipt.forma_pago)}</span>
                                    </div>
                                    <div className="receipt-row">
                                        <span className="row-label">Estado:</span>
                                        <span className="row-value">{getStatusBadge(selectedReceipt.estado)}</span>
                                    </div>
                                </div>

                                {selectedReceipt.solicita_factura == 1 && (
                                    <div className="receipt-section" style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div className="receipt-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1e293b', marginBottom: '1rem', borderBottom: 'none', paddingBottom: 0 }}>
                                            <FileText size={16} /> Estado de Facturación
                                        </div>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', margin: 0 }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedReceipt.factura_entregada == 1}
                                                onChange={() => toggleInvoiceStatus(selectedReceipt.id, selectedReceipt.factura_entregada == 1)}
                                                style={{ width: '16px', height: '16px', accentColor: '#00BBBF', cursor: 'pointer' }}
                                            />
                                            <span style={{ color: selectedReceipt.factura_entregada == 1 ? '#047857' : '#475569', fontWeight: '500', fontSize: '14px' }}>
                                                {selectedReceipt.factura_entregada == 1 ? 'Factura Entregada al Paciente' : 'Marcar Factura como Entregada'}
                                            </span>
                                        </label>
                                    </div>
                                )}

                                {selectedReceipt.notas && (
                                    <div className="receipt-section">
                                        <div className="receipt-section-title">Notas</div>
                                        <p className="receipt-notes">{selectedReceipt.notas}</p>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn-secondary" onClick={() => setSelectedReceipt(null)}>
                                    Cerrar
                                </button>
                                <button className="btn-primary" onClick={() => handlePrint(selectedReceipt)}>
                                    <Printer size={16} /> Imprimir
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default ReceiptHistory;

